let api = null;

const initApi = () => {
  if (api) {
    return api;
  }

  api = new window.AdminAPI();

  return api;
};

const parseJsonArrayFirst = (value) => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && 0 < parsed.length) {
      return parsed[0];
    }
    return null;
  } catch {
    return null;
  }
};

const RECIPE_FIELDS_FRAGMENT = `
  fragment RecipeFields on Metaobject {
    id
    handle
    displayName
    fields {
      key
      value
      type
      reference {
        ... on Metaobject {
          id
          handle
          displayName
        }
        ... on Page {
          id
          handle
          title
          publishedAt
        }
      }
      references(first: 10) {
        nodes {
          ... on Metaobject {
            id
            handle
            displayName
          }
          ... on MediaImage {
            id
            image {
              url
              altText
            }
          }
          ... on GenericFile {
            id
            url
          }
        }
      }
    }
  }
`;

const RECIPE_QUERY = `
  query GetRecipesBySubcategory($queryString: String!, $first: Int!, $after: String) {
    metaobjects(
      type: "recept",
      first: $first,
      after: $after,
      query: $queryString,
      sortKey: "id",
      reverse: true
    ) {
      nodes {
        ...RecipeFields
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${RECIPE_FIELDS_FRAGMENT}
`;

const RECIPES_BY_IDS_QUERY = `
  query GetRecipesByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ...RecipeFields
    }
  }
  ${RECIPE_FIELDS_FRAGMENT}
`;
const TRANSLATIONS_QUERY = `
  query GetRecipeTranslations($ids: [ID!]!, $first: Int!, $locale: String!) {
    translatableResourcesByIds(resourceIds: $ids, first: $first) {
      nodes {
        resourceId
        translations(locale: $locale) {
          key
          value
        }
      }
    }
  }
`;

const RECIPE_FIELD_TRANSLATIONS = [
  { key: 'titel_recept', prop: 'title' },
  { key: 'bereidingstijd', prop: 'cookTime' },
  // difficulty is a list field, so its value comes back as a JSON array string (e.g. '["Makkelijk"]') — parse out the first entry
  { key: 'moeilijkheidsgraad', prop: 'difficulty', transform: parseJsonArrayFirst }
];

const PAGE_FIELD_TRANSLATIONS = [
  { key: 'handle', prop: 'handle' }
];

const buildFieldsMap = (fields) => {
  const fieldsMap = {};
  fields.forEach((field) => {
    fieldsMap[field.key] = {
      value: field.value,
      type: field.type,
      reference: field.reference,
      references: field.references?.nodes || []
    };
  });
  return fieldsMap;
};

const extractImageUrl = (fotosField) => {
  if (!fotosField?.references?.length) {
    return null;
  }

  const firstRef = fotosField.references[0];
  if (firstRef?.image?.url) {
    return firstRef.image.url;
  }

  if (firstRef?.url) {
    return firstRef.url;
  }

  return null;
};

const transformRecipeNode = (node) => {
  const fieldsMap = buildFieldsMap(node.fields);
  return ({
    id: node.id.split('/').pop(),
    gid: node.id,
    handle: node.handle,
    displayName: node.displayName,
    fields: fieldsMap,
    page: fieldsMap.page?.reference,
    title: fieldsMap.titel_recept?.value || node.displayName,
    image: extractImageUrl(fieldsMap.fotos),
    cookTime: fieldsMap.bereidingstijd?.value || null,
    difficulty: parseJsonArrayFirst(fieldsMap.moeilijkheidsgraad?.value),
    subcategories: fieldsMap.subcategorieen?.references || []
  });
};

const fetchRecipeTranslations = async (ids, locale) => {
  if (!ids.length) {
    return {};
  }

  try {
    const data = await initApi().query(TRANSLATIONS_QUERY, {
      ids,
      first: ids.length,
      locale
    });

    const map = {};

    (data.translatableResourcesByIds?.nodes || []).forEach((node) => {
      const translatedFields = {};
      (node.translations || []).forEach((translation) => {
        translatedFields[translation.key] = translation.value;
      });
      map[node.resourceId] = translatedFields;
    });

    return map;
  } catch (error) {
    return {};
  }
};

const applyTranslatedFields = (target, translatedFields, mappings) => {
  if (!target || !translatedFields) {
    return;
  }

  mappings.forEach(({ key, prop, transform }) => {
    const value = translatedFields[key];
    if (!value) {
      return;
    }

    target[prop] = transform ? transform(value) : value;
  });
};

const localizeRecipes = async (recipes, locale) => {
  const ids = [];
  recipes.forEach((recipe) => {
    if (recipe.gid) {
      ids.push(recipe.gid);
    }
    if (recipe.page?.id) {
      ids.push(recipe.page.id);
    }
  });

  const translationsMap = await fetchRecipeTranslations(ids, locale);

  recipes.forEach((recipe) => {
    applyTranslatedFields(recipe, translationsMap[recipe.gid], RECIPE_FIELD_TRANSLATIONS);
    applyTranslatedFields(recipe.page, translationsMap[recipe.page?.id], PAGE_FIELD_TRANSLATIONS);
  });
};

const fetchRecipePage = async (subcategoryId, limit, cursor = null, translationLocale = null) => {
  const sanitizedId = subcategoryId.replace(/"/g, '\\"');
  const collected = [];
  const state = { currentCursor: cursor, hasNextPage: true };

  while (collected.length < limit && state.hasNextPage) {
    const variables = {
      queryString: `fields.subcategorieen:"${sanitizedId}"`,
      first: limit,
      after: state.currentCursor
    };

    const data = await initApi().query(RECIPE_QUERY, variables);
    const pageInfo = data.metaobjects.pageInfo;

    const published = data.metaobjects.nodes
      .map(transformRecipeNode)
      .filter((recipe) => recipe.page && recipe.page.publishedAt);

    collected.push(...published);
    state.hasNextPage = pageInfo.hasNextPage;
    state.currentCursor = pageInfo.endCursor;
  }

  const pageItems = collected.slice(0, limit);
  const hasMore = state.hasNextPage || collected.length > limit;

  if (translationLocale && pageItems.length) {
    await localizeRecipes(pageItems, translationLocale);
  }

  return ({
    items: pageItems,
    hasNextPage: hasMore,
    endCursor: state.currentCursor
  });
};

const METAOBJECT_GID_PREFIX = 'gid://shopify/Metaobject/';
const MAX_IDS_PER_REQUEST = 250;
const publishedRecipesByIds = new Map();

const toMetaobjectGid = (id) => {
  return id.startsWith('gid://') ? id : `${METAOBJECT_GID_PREFIX}${id}`;
};

const loadPublishedRecipesByIds = (recipeIds) => {
  const cacheKey = recipeIds.join(',');

  if (!publishedRecipesByIds.has(cacheKey)) {
    const gids = recipeIds.slice(0, MAX_IDS_PER_REQUEST).map(toMetaobjectGid);
    const request = initApi().query(RECIPES_BY_IDS_QUERY, { ids: gids })
      .then((data) => (data.nodes || [])
        .filter(Boolean)
        .map(transformRecipeNode)
        .filter((recipe) => recipe.page && recipe.page.publishedAt));

    // Do not cache failures, so a retry can succeed
    request.catch(() => publishedRecipesByIds.delete(cacheKey));
    publishedRecipesByIds.set(cacheKey, request);
  }

  return publishedRecipesByIds.get(cacheKey);
};

const fetchRecipePageByIds = async (recipeIds, limit, cursor = null, translationLocale = null) => {
  const published = await loadPublishedRecipesByIds(recipeIds);
  const offset = cursor || 0;
  const pageItems = published.slice(offset, offset + limit).map((recipe) => ({ ...recipe, page: { ...recipe.page } }));
  const nextOffset = offset + limit;
  const hasNextPage = nextOffset < published.length;

  if (translationLocale && pageItems.length) {
    await localizeRecipes(pageItems, translationLocale);
  }

  return ({
    items: pageItems,
    hasNextPage,
    endCursor: hasNextPage ? nextOffset : null
  });
};

const getFieldValue = (recipe, fieldKey) => {
  return recipe.fields[fieldKey]?.value;
};

window.RecipeAPI = {
  fetchRecipePage,
  fetchRecipePageByIds,
  getFieldValue
};
